/*
 * File: DateInput.tsx
 * Author: Austin Robinson
 * Date Created: 10/18/2023
 * Copyright (c) DrivingDoc 2023
 */
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker, DateTimePickerProps } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import { Controller, useFormContext } from 'react-hook-form';

interface PropTypes extends DateTimePickerProps<Dayjs> {
  name: string;
  label: string;
  helperText?: string;
  clearable?: boolean;
}

export default function DateTimeInput({ name, label, helperText, clearable, ...props }: PropTypes) {
  const { control } = useFormContext();
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState }) => (
          <DateTimePicker
            label={label}
            slotProps={{
              textField: {
                variant: 'standard',
                fullWidth: true,
                InputLabelProps: { shrink: true },
                error: !!fieldState.error,
                helperText: fieldState.error ? fieldState.error?.message : helperText,
                onBlur: field.onBlur,
              },
              ...(clearable ? { field: { clearable: true } } : {}),
            }}
            inputRef={field.ref}
            value={field.value ? dayjs(field.value) : null}
            onChange={(newValue) => {
              if (newValue?.isValid()) {
                // round to the start of the minute since it is the smallest unit
                field.onChange(dayjs(newValue).startOf('minute').toISOString());
              } else if (!newValue) {
                field.onChange(null);
              }
            }}
            onError={(newError) => console.log(`Date Error: ${newError}`)}
            {...props}
          />
        )}
      />
    </LocalizationProvider>
  );
}
